import { z } from "zod";

// TES-362 — the canonical deep-link path table, shared across authoring
// (admin picker + zod validation), the API's derived producers, and the
// mobile resolver. Before this, `ctaPath` was a free string validated nowhere:
// an author writing `carebyella://weekly_checkin` (underscore) got a button
// that silently did nothing.
//
// The mobile route names stay a mobile concern (apps/mobile/src/patient/
// deep-link.ts maps path → screen); a mobile unit test asserts its map covers
// exactly this table, so the two can't drift.

// Static paths (no parameters). `billing` is deliberately absent: subscription
// purchase/manage is web-only on mobile (CAR-69 / Apple 3.1.1) — authors
// should link the web URL instead.
export const DEEP_LINK_STATIC_PATHS = [
  "weekly-checkin",
  "health-declaration",
  "blood-test",
  // TES-1287 — "Om ditt blodprov", the blood-test articles moved off
  // FindLab. App 2.0.9; an older build treats it as an unknown path (a
  // no-op button), so nothing a 2.0.8 patient sees may depend on it.
  "blood-test/about",
  "treatment",
  // Ö-140 — CP-12's card and the injection-guide article both land on the
  // first-dose registration, where the patient confirms the DATE she took it.
  "first-dose",
  // TES-1160 — "Registrera dos" from Hem's dose-day task
  // ("Dags för veckans dos"). App 2.0.7; the card is served only to clients
  // at CLIENT_CONTRACT_CHANGES.doseDayTask.
  "dose",
  // TES-1527 (Ö-776) — "Registrera tagen dos": a dose she took today or
  // earlier, whatever the planned day. Only a build at
  // CLIENT_CONTRACT_CHANGES.registerTakenDose knows it, and only such a
  // build is sent the link on "Din behandlingsvecka".
  "dose/taken",
  // TES-1429 (TES-1425 §3, §7) — "Din behandlingsvecka", the destination of
  // the planned dose day's push (`dose_reminder`): Hem, scrolled to the
  // card. Only a build at CLIENT_CONTRACT_CHANGES.treatmentWeekCard knows
  // it, and only such a build is sent that push.
  "treatment-week",
  // TES-675 — CP-13's card links to the safety view:
  // "Visa stöd och viktiga nummer". The numbers are in the card's own text
  // too; this is the way back to the full information, not the only place it
  // exists.
  "acute-safety",
  // TES-998 (Ö-238) — the goal-zone choice: "Vad vill du fokusera på nu?"
  // when a weight has reached her goal in active treatment. App 2.0.2+; an
  // older build treats it as an unknown path (a no-op button).
  "goal-zone",
  "chat",
  // Chat split — direct thread targets. Bare `chat` opens the conversation
  // list; notification/candidate CTAs use these so they land
  // "direkt till rätt flöde", never the generic list.
  "chat/doctor",
  "chat/coach",
  // TES-1549 (Ö-625) — "Visa ärende": "Meddelanden → Hjälp", her Ella Support
  // thread, where a medical question she sent is the case. Only a build at
  // CLIENT_CONTRACT_CHANGES.restartRequest knows it.
  "chat/support",
  // TES-1657 (Ö-1046 b) — "Ditt medicinska team" under Meddelanden, where the
  // doctor's welcome and the F-01 card are: Hem's F-01 card opens it. Only a
  // build at CLIENT_CONTRACT_CHANGES.onboardingChannel knows it, and only
  // such a build is sent the link.
  "chat/medical-team",
  // TES-1549 (Ö-625) — "Jag vill återuppta behandlingen": the existing
  // "Medicinsk fråga" form, with the restart topic. Same gate.
  "restart-request",
  // TES-1634 — "Så fungerar receptförnyelsen" on the week-3 renewal card:
  // the explanation of how the renewal works. Only a build at
  // CLIENT_CONTRACT_CHANGES.renewalExplainer knows it, and only such a build
  // is sent the link.
  "renewal/how-it-works",
  // TES-1648 (Ö-1016) — the written track's photo task and its
  // instructions, from Hem's "Lämna dina bilder". Only a build at
  // CLIENT_CONTRACT_CHANGES.assessmentPhotos knows them, and only such a
  // build is sent the card.
  "assessment-photos",
  "assessment-photos/how-to",
  // TES-1649 (Ö-1014) — the written track's waist task and its instruction,
  // from Hem's "Mät ditt midjemått". Only a build at
  // CLIENT_CONTRACT_CHANGES.assessmentWaist knows them, and only such a
  // build is sent the card.
  "assessment-waist",
  "assessment-waist/how-to",
  // TES-1279 — "Hjälp och kontakt" (app 2.0.9): Kontakta Ella, her replies
  // from Ella and the doctor's earlier notices. An older build treats it as
  // an unknown path (a no-op button), so a server-emitted CTA to it must be gated
  // on CLIENT_CONTRACT_CHANGES.coachAndHelp.
  "help",
  // TES-380: the "Min resa" journey timeline (mobile screen MinResa).
  "min-resa",
  // TES-590 — CP-06 "Komplettera ditt medicinska underlag".
  "medical-evidence",
  // TES-1286 — Hem's CP-06 cards open the action itself (app 2.0.9): the
  // blood-test payment and the blood-pressure form. An older build treats
  // them as unknown paths (a no-op), so the cards' single `ctaPath` that
  // 2.0.8 follows stays `medical-evidence`.
  "medical-evidence/blood-pressure",
  "lab-payment",
  // TES-591 — the subscription lock's information view.
  "subscription-info",
  // TES-390 §7 (feedback lives where the data lives): the lab-results view
  // with the doctor's feedback inline — notification CTAs land HERE, not on a
  // generic surface.
  "provsvar",
  // TES-389 follow-up (F-01 app-first): the suitability quiz (CP-00A/B CTAs)…
  "f01",
  // TES-1650 (Ö-1017) — F-01's re-confirmation of the time-sensitive answers
  // before the written-track hand-over; back to Hem when confirmed. Only a
  // build at CLIENT_CONTRACT_CHANGES.writtenHandoverReconfirm knows it, and
  // only such a build is sent the link.
  "f01/reconfirm",
  // TES-1663 — "Uppdatera mina svar" under the negative F-01 feedback in
  // "Ditt medicinska team": F-01 with every answer prefilled, a new
  // versioned result (the saved result's "Uppdatera mina uppgifter"). Only a
  // build at CLIENT_CONTRACT_CHANGES.negativeF01Channel is sent it.
  "f01/update",
  // …and its summary view ("första sammanfattningen", CP-00C's CTA). A static
  // path, not a min-resa parameter — the app routes it to the F01 screen's
  // summary state.
  "min-resa/f01-summary",
  // v15 (CP-01): the combined pick-a-time → pay → confirmed booking flow
  // (TES-412). Routes to the slot picker.
  "book-doctor-meeting",
  // TES-456 (F-07): the transfer paperwork — preparation page, the form, and
  // its submitted/"under review" state all live on one screen.
  "prescription-transfer",
  // The "Stöd" programmes: section screens for "Kost" + "Träning".
  "support/nutrition",
  "support/training",
  "support/behavior-wellbeing",
  "support/behavior/courses",
  // Structured recipe experience: the recipe library under "Stöd → Kost".
  // Individual recipes are reached via the parameterised form
  // carebyella://recipe/<key> (like article/<key>, not a static path).
  "support/nutrition/recipes",
  // "Stöd" IA v2.1: the area subpages — plans + the combined knowledge
  // libraries.
  "support/nutrition/plans",
  "support/nutrition/knowledge",
  "support/training/plans",
  // TES-612: the programme overview is its own page again
  // ("Träning ▸ Träningspass"). The older "/plans" alias points to
  // the same page.
  "support/training/passes",
  "support/training/knowledge",
  // CP-33: the doctor's published monthly feedback — a dedicated
  // case-bound view; NOT the labs view (the content is the monthly review +
  // dose/prescription decision, not a lab result).
  "medical-review/monthly/latest",
  // "Hälsa" v3 (destination_registry.csv). The four canonical "Hälsa" surfaces.
  // `health/journey` and `min-resa` resolve to the SAME screen: "Din resa" is
  // the patient label, min-resa is the legacy path already live in published
  // content, and breaking it would kill working CTAs.
  "health/home",
  "health/trends",
  "health/journey",
  "health/care",
  // TES-561: "Din vård ▸ Behandlingsbeslut" (CP-09 "Läs behandlingsbeslutet").
  "health/care/decision",
  // TES-484 (appdesign 06): the women's health ("Kvinnohälsa") hub and its
  // three topic pages.
  "womens-health",
  "womens-health/cycle",
  "womens-health/pcos",
  "womens-health/menopause",
] as const;
export type DeepLinkStaticPath = (typeof DEEP_LINK_STATIC_PATHS)[number];

// Parameterized prefixes: `article/<key>` (TES-351) and `milestone/<1|3|6|9|12>`
// (TES-360). The suffix is validated loosely here (non-empty; numeric for
// milestone) — existence of the article key is a referential check that
// belongs to import/publish validation, not the path grammar.
export const DEEP_LINK_PARAM_PREFIXES = [
  "article/",
  "milestone/",
  "recipe/",
] as const;

const SCHEME = "carebyella://";
const URL_RE = /^https?:\/\//i;

// Is `path` a deep link the app can actually resolve? Accepts:
//   - carebyella://<static path>          (the table above)
//   - carebyella://article/<key>          (non-empty key)
//   - carebyella://milestone/<digits>
//   - http(s)://…                          (opened in the browser)
// Everything else — including bare route names, which only happen to work
// when they match a React Navigation screen — is rejected for AUTHORED
// content so imports fail loud instead of shipping dead buttons.
export function isResolvableCtaPath(path: string): boolean {
  if (URL_RE.test(path)) return true;
  if (!path.toLowerCase().startsWith(SCHEME)) return false;
  const rest = path.slice(SCHEME.length);
  if ((DEEP_LINK_STATIC_PATHS as readonly string[]).includes(rest)) return true;
  if (/^article\/.+$/i.test(rest)) return true;
  if (/^milestone\/\d+$/i.test(rest)) return true;
  // Strukturerad receptupplevelse: carebyella://recipe/<key>.
  if (/^recipe\/.+$/i.test(rest)) return true;
  return false;
}

// Zod refinement for authored CTA paths (flow item content, weekly themes…).
export const resolvableCtaPathSchema = z
  .string()
  .min(1)
  .refine(isResolvableCtaPath, {
    message:
      "ctaPath must be a known deep link (carebyella://weekly-checkin|health-declaration|blood-test|treatment|chat|min-resa, carebyella://article/<key>, carebyella://milestone/<n>) or an http(s) URL",
  });

// ---------------------------------------------------------------------------
// TES-852 — the web→app handoff link
// ---------------------------------------------------------------------------

// `carebyella://lead/<sessionId>` — how an ANONYMOUS web quiz reaches the app.
//
// The problem it solves: her F-01 answers live on a `Lead` row keyed only by
// `sessionId`, and that value exists in exactly one place — an httpOnly cookie
// bound to the web origin. If she identifies with BankID on the WEB, the web
// links the lead itself and the app finds her answers by `patientId` like
// everything else. If she never identifies there and signs in from the APP
// instead, nothing connects the two and she starts F-01 over.
//
// `Lead` carries no personnummer and no email — the quiz is answered before
// BankID, so the API never learns who she is until she identifies. There is
// therefore no identity to join on, and this link is the carrier instead: the
// anonymous session itself, crossing the origin boundary by being handed to
// her.
//
// DELIBERATELY NOT in DEEP_LINK_STATIC_PATHS or DEEP_LINK_PARAM_PREFIXES.
// Those are the grammar for AUTHORED content, and a claim link is not content
// — letting an article CTA mint one would let authored copy hand a reader
// somebody else's answers. `isResolvableCtaPath` rejects this shape on
// purpose; the funnel builds it in code.
const LEAD_HANDOFF_PREFIX = "lead/";

// Same shape the API enforces before it touches the table (TES-716): the web
// mints it with crypto.randomUUID, so anything that is not a v4 UUID is not
// one of ours. Validated on BOTH sides — the builder must not emit a link the
// API will refuse, and the parser must not hand the app a value to claim with
// that came from a hand-typed or tampered URL.
const SESSION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * The handoff link for a quiz session, or null when the id is not one of ours.
 *
 * Null rather than a thrown error or a bare `carebyella://`: the caller is a
 * funnel page deciding whether to offer "continue in the app", and a missing
 * or malformed session means it offers the ordinary app link instead.
 */
export function buildLeadHandoffLink(sessionId: string): string | null {
  return SESSION_ID_RE.test(sessionId)
    ? `${SCHEME}${LEAD_HANDOFF_PREFIX}${sessionId}`
    : null;
}

/**
 * The session id inside a handoff link, or null if this is not one.
 *
 * Fail closed: anything that is not exactly `carebyella://lead/<v4 uuid>`
 * returns null and is treated as "no claim to make". The app must never carry
 * a value it cannot recognise to the claim endpoint — the endpoint links one
 * person's health answers into another person's record, so a URL that is
 * merely close enough is not close enough.
 *
 * Query strings and fragments are rejected rather than trimmed off, for the
 * same reason: a link that carries anything extra is not one we built.
 */
export function parseLeadHandoffLink(url: string): string | null {
  if (!url.toLowerCase().startsWith(SCHEME)) return null;
  const rest = url.slice(SCHEME.length).replace(/^\/+/, "");
  if (!rest.toLowerCase().startsWith(LEAD_HANDOFF_PREFIX)) return null;
  const sessionId = rest.slice(LEAD_HANDOFF_PREFIX.length);
  return SESSION_ID_RE.test(sessionId) ? sessionId.toLowerCase() : null;
}

const ARTICLE_PREFIX = "article/";

/**
 * The article key inside an article deep link, or null if this is not one.
 *
 * TES-866 — the Home question section needs the DESTINATION, not the link: an
 * entry is a question that opens an article, so the renderer navigates by key
 * the same way the Kunskapsbank does.
 *
 * Fail closed like `parseLeadHandoffLink`. An entry whose destination cannot
 * be read is dropped by the caller rather than rendered as text that looks
 * tappable and is not — a dead question in a list of working ones is worse
 * than one question fewer.
 *
 * Query strings and fragments are rejected rather than trimmed, so a link
 * carrying anything extra is not treated as one we built.
 */
export function parseArticleLink(url: string): string | null {
  if (!url.toLowerCase().startsWith(SCHEME)) return null;
  const rest = url.slice(SCHEME.length).replace(/^\/+/, "");
  if (!rest.toLowerCase().startsWith(ARTICLE_PREFIX)) return null;
  const key = rest.slice(ARTICLE_PREFIX.length);
  if (key.length === 0) return null;
  if (/[?#/\s]/.test(key)) return null;
  return key;
}
